import type { DictionaryValidationFeedsStatus } from "@shared-core/data/cost-engine-validation-types";
import { formatRollingValidationSetSummary } from "@shared-core/domain/cost-engine-validation-presentation";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { Wifi } from "lucide-react";

/**
 * The weekly run's health (design brief §8, journey 1; review decisions in
 * `02_glossary_and_live_costing.md`): one card naming the set size, the
 * weekly budget used, and last/next run, and never hiding an over-budget set.
 * The run asks its set of in-house costs weekly and checks every in-house
 * answer (catalogue management in One, P16); "Rolling validation" keeps its
 * name until Live costing's own package renames the area. Lives on the Live
 * costing › Checks tab only.
 */
export function FeedsBanner({
  status,
}: {
  status: DictionaryValidationFeedsStatus;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5 font-medium">
          <Wifi
            className={`size-3.5 ${status.rollingValidation.healthy ? "text-[color:var(--color-success)]" : "text-destructive"}`}
          />
          Rolling validation
        </span>
        <span className="text-muted-foreground">
          {formatRollingValidationSetSummary(status)}
        </span>
        <span className="text-xs text-muted-foreground">
          {status.rollingValidation.mintsUsedThisWeek ?? 0} of{" "}
          {status.rollingValidation.mintBudgetThisWeek ?? 0} checks used this
          week · last {formatDateTimeOrDash(status.rollingValidation.lastRunAt)}{" "}
          · next{" "}
          {formatDateTimeOrDash(status.rollingValidation.nextScheduledAt)}
        </span>
        {/* Scoped runs (`cis cost-check run --item …`) spend the same weekly
            allowance as the scheduled run, so the figure above counts them —
            naming them here is what makes the figure explainable rather than
            merely higher than expected. */}
        {/* Quarantined costs (spec 03 §5.1 step 4c) are not in the set
            size above: no run asks for them until someone releases them on
            Pollards (in-house), so they wait on a person, not on the budget. */}
        {status.quarantinedPriceCount > 0 && (
          <span className="text-xs font-medium text-destructive">
            {status.quarantinedPriceCount}{" "}
            {status.quarantinedPriceCount === 1 ? "price" : "prices"}{" "}
            quarantined after a Tharstern timeout — release on Pollards
            (in-house)
          </span>
        )}
        {(status.rollingValidation.scopedRunsThisWeek ?? []).length > 0 && (
          <span className="text-xs text-muted-foreground">
            Including{" "}
            {(status.rollingValidation.scopedRunsThisWeek ?? [])
              .map(
                (run) =>
                  `${run.scope} (${run.mintsAttempted} mint${run.mintsAttempted === 1 ? "" : "s"})`,
              )
              .join(", ")}
          </span>
        )}
      </div>
    </div>
  );
}

/** Couldn't load feed status — the caller's fallback banner, shared so both
 *  pages show the same wording. */
export function FeedsBannerError() {
  return (
    <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
      Couldn&rsquo;t load the weekly run&rsquo;s status. Cost-check figures
      below may be stale.
    </p>
  );
}
